import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { btns } from './buttons';

/**
 * Shared "there's nothing here yet" state — the sibling of {@link QueryErrorState}
 * (same centered layout, so an empty result and a failed fetch read as a matched
 * pair rather than two ad-hoc treatments). Replaces the bare one-line
 * "No X yet." paragraphs scattered across the pillar pages: icon + one line of
 * context + an optional primary action that opens the page's own add/upload/
 * connect flow.
 *
 * Composition note (first-run FR3): this renders as a self-contained block with
 * no outer margin assumptions, so a per-page `PageIntro` subhead (UX2) can sit
 * directly above it on the same surface without fighting for space.
 *
 * The action is either an in-app navigation (`to`) or a callback (`onAction`,
 * e.g. toggling an inline add form) — pass one, not both.
 *
 * `secondaryAction` (steward-app#579) is for the surfaces with two genuine ways in,
 * where suppressing one would be misleading rather than merely terse: Accounts can be
 * connected from an institution OR entered by hand, and an empty state that offered only
 * the second is how a user concluded there was no way to connect one at all. Outline
 * styling keeps the ranking unambiguous.
 */
export interface EmptyStateAction {
  label: string;
  /** Navigate to a route (renders a <Link>). */
  to?: string;
  /** Run a handler (renders a <button>) — e.g. open an inline GuardedForm. */
  onAction?: () => void;
  /** Disables an `onAction` button while its work is in flight (e.g.
   *  `FirmWorkspaceNotReady`'s "Check again" while its recheck request is out) — steward-app#1271.
   *  No effect on a `to` action: a `<Link>` has no disabled state to show. */
  disabled?: boolean;
}

function ActionControl({ action, className }: { action: EmptyStateAction; className: string }) {
  return action.to
    ? <Link to={action.to} className={className}>{action.label}</Link>
    : <button onClick={action.onAction} disabled={action.disabled} className={className}>{action.label}</button>;
}

export function EmptyState({
  icon: Icon,
  message,
  action,
  secondaryAction,
  'data-testid': testId,
}: {
  icon: LucideIcon;
  message: string;
  action?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  'data-testid'?: string;
}) {
  return (
    <div
      data-testid={testId}
      className="flex flex-col items-center gap-3 text-center py-10 px-4"
    >
      <Icon size={20} strokeWidth={1.75} className="text-slate" aria-hidden />
      <p className="text-slate text-[13px] font-sans m-0 max-w-sm">{message}</p>
      {(action || secondaryAction) && (
        <div className="flex flex-wrap items-center justify-center gap-2">
          {action && <ActionControl action={action} className={btns.btnPrim} />}
          {secondaryAction && <ActionControl action={secondaryAction} className={btns.btn} />}
        </div>
      )}
    </div>
  );
}
